import { zodResolver } from '@hookform/resolvers/zod'
import {
  Brain,
  Clock,
  Gauge,
  HelpCircle,
  Globe,
  Hash,
  Layers,
  ListChecks,
  MessageSquare,
  Minus,
  Plus,
  Shapes,
  Sparkles,
  Wand2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { SuggestPromptButton } from '@/features/factory/components/SuggestPromptButton'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import {
  useStartQuiz,
  useSuggestPrompt,
  type QuizInput,
  type QuizVideoType,
} from '@/api/endpoints/factories'
import { useThemes, type Theme } from '@/api/endpoints/themes'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { videoFormatsFor } from '@/lib/videoFormats'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { NarratorPicker } from '../_shared/NarratorPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'
import { FORMAT_RULES, type SecondsKey, formatDefaults, secondsLabel } from './formatRules'

const FIELD_LABELS: Record<string, string> = {
  theme: 'Tema',
  count: 'Quantidade',
  language: 'Idioma',
  custom_prompt: 'Prompt',
  num_questions: 'Perguntas / rodadas',
  seconds_per_question: 'Tempo por pergunta',
  difficulty: 'Dificuldade',
  options_per_question: 'Alternativas',
  video_type: 'Formato',
  tone: 'Tom',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
}

// Rótulos vêm de lib/videoFormats — a mesma fonte da lista de prioridade do
// agendador, para o form e o agendador dizerem a mesma coisa.
const VIDEO_TYPES: ReadonlyArray<SegmentedOption<QuizVideoType>> = videoFormatsFor('quiz').map(
  (f) => ({ value: f.value as QuizVideoType, label: f.label }),
)

type LanguageKey = 'pt-br' | 'en' | 'both'
const LANGUAGES: ReadonlyArray<SegmentedOption<LanguageKey>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type DifficultyKey = 'easy' | 'medium' | 'hard'
const DIFFICULTIES: ReadonlyArray<SegmentedOption<DifficultyKey>> = [
  { value: 'easy', label: 'Fácil' },
  { value: 'medium', label: 'Médio' },
  { value: 'hard', label: 'Difícil' },
]

type OptionsKey = '2' | '3' | '4'

type ToneKey = 'formal' | 'curious' | 'sarcastic'
const TONES: ReadonlyArray<SegmentedOption<ToneKey>> = [
  { value: 'formal', label: 'Formal' },
  { value: 'curious', label: 'Curioso' },
  { value: 'sarcastic', label: 'Debochado' },
]

const CUSTOM_THEME_OPTION = { value: 'custom', label: 'Prompt customizado' } as const

const schema = z
  .object({
    theme: z.string().min(1, 'Escolha um tema ou prompt'),
    count: z.number().int().min(1).max(10),
    language: z.enum(['pt-br', 'en', 'both']),
    num_questions: z.number().int().min(2).max(10),
    seconds_per_question: z.number().int().min(3).max(10),
    difficulty: z.enum(['easy', 'medium', 'hard']),
    options_per_question: z.number().int().min(2).max(4),
    video_type: z.enum(['trivia', 'choice']),
    custom_prompt: z.string().trim().optional(),
    tone: z.enum(['formal', 'curious', 'sarcastic']).optional(),
    tts_voice_pt: z.string().optional(),
    tts_voice_en: z.string().optional(),
    llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
  })
  .superRefine((v, ctx) => {
    const rules = FORMAT_RULES[v.video_type]
    const len = v.custom_prompt?.length ?? 0
    // Vazio é válido nos dois formatos: o backend gera o prompt pelo
    // sugeridor, que tem uma variante de dilemas para a Escolha.
    if (v.theme === 'custom' && len > 0 && len < 10) {
      ctx.addIssue({
        code: 'custom',
        path: ['custom_prompt'],
        message: 'Descreva com pelo menos 10 caracteres, ou deixe em branco para gerar um automaticamente.',
      })
    }
    const [minRounds, maxRounds] = rules.rounds
    if (v.num_questions < minRounds || v.num_questions > maxRounds) {
      ctx.addIssue({
        code: 'custom',
        path: ['num_questions'],
        message: `Entre ${minRounds} e ${maxRounds}.`,
      })
    }
    if (!rules.options.includes(String(v.options_per_question) as OptionsKey)) {
      ctx.addIssue({
        code: 'custom',
        path: ['options_per_question'],
        message: `Use ${rules.options.join(' ou ')} alternativas.`,
      })
    }
    const [minSeconds, maxSeconds] = rules.secondsRange
    if (v.seconds_per_question < minSeconds || v.seconds_per_question > maxSeconds) {
      ctx.addIssue({
        code: 'custom',
        path: ['seconds_per_question'],
        message: `Entre ${secondsLabel(String(minSeconds) as SecondsKey)} e ${secondsLabel(String(maxSeconds) as SecondsKey)}.`,
      })
    }
    if (v.language === 'both' && !rules.allowBothLanguages) {
      ctx.addIssue({
        code: 'custom',
        path: ['language'],
        message: 'A Escolha gera um idioma por vez.',
      })
    }
  })

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  theme: 'quiz-capitals',
  count: 1,
  language: 'pt-br',
  difficulty: 'medium',
  video_type: 'trivia',
  custom_prompt: '',
  ...formatDefaults('trivia'),
}

/** Form snapshot → preset payload. Strips ``channel`` / ``count`` (the
 * resolver does the same server-side). */
function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    theme: values.theme,
    language: values.language,
    video_type: values.video_type,
    num_questions: values.num_questions,
    seconds_per_question: values.seconds_per_question,
    difficulty: values.difficulty,
    options_per_question: values.options_per_question,
    custom_prompt: values.theme === 'custom' && values.custom_prompt ? values.custom_prompt : '',
  }
  if (values.tone) payload.tone = values.tone
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  const videoType: QuizVideoType = params.video_type === 'choice' ? 'choice' : 'trivia'
  // Preset antigo não tem video_type: é trivia, com os defaults da trivia.
  const defaults = formatDefaults(videoType)
  const v: FormValues = {
    ...FORM_DEFAULTS,
    video_type: videoType,
    theme: (params.theme as string | undefined) ?? FORM_DEFAULTS.theme,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    num_questions: (params.num_questions as number | undefined) ?? defaults.num_questions,
    seconds_per_question:
      (params.seconds_per_question as number | undefined) ?? defaults.seconds_per_question,
    difficulty:
      (params.difficulty as FormValues['difficulty'] | undefined) ?? FORM_DEFAULTS.difficulty,
    options_per_question:
      (params.options_per_question as number | undefined) ?? defaults.options_per_question,
    custom_prompt: (params.custom_prompt as string | undefined) ?? '',
  }
  if (params.tone) v.tone = params.tone as FormValues['tone']
  if (params.tts_voice_pt) v.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) v.tts_voice_en = params.tts_voice_en as string
  return v
}

export function QuizForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const { data: allThemes = [], isPending: themesPending } = useThemes()

  const startMutation = useStartQuiz()
  const suggestPrompt = useSuggestPrompt()

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...FORM_DEFAULTS,
      theme: allThemes.find((t: Theme) => t.slug.startsWith('quiz-'))?.slug ?? FORM_DEFAULTS.theme,
      llm_provider: 'auto',
    },
  })

  const videoType = form.watch('video_type')
  const rules = FORMAT_RULES[videoType]
  // Cada formato tem o seu namespace de temas em config/themes/ (quiz-* e
  // choice-*). O endpoint devolve todos os JSON, então filtramos pelo prefixo
  // — o mesmo truque do AtlasForm com atlas-*.
  const themes = allThemes.filter((t: Theme) => t.slug.startsWith(rules.themePrefix))

  // Erros de campos sem mensagem própria na tela. Sem isto um preset fora dos
  // limites travava o "Gerar" sem dizer por quê.
  const knobErrors = (
    ['num_questions', 'options_per_question', 'seconds_per_question', 'language'] as const
  )
    .map((field) => {
      const message = form.formState.errors[field]?.message
      return message ? `${FIELD_LABELS[field] ?? field}: ${message}` : null
    })
    .filter((m): m is string => m !== null)

  const theme = form.watch('theme')
  const count = form.watch('count')
  const language = form.watch('language')
  const numQuestions = form.watch('num_questions')
  const secondsPerQuestion = form.watch('seconds_per_question')
  const difficulty = form.watch('difficulty')
  const optionsPerQuestion = form.watch('options_per_question')
  const tone = form.watch('tone')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const llmProvider = form.watch('llm_provider')

  const [minRounds, maxRounds] = rules.rounds
  const secondsOptions: ReadonlyArray<SegmentedOption<SecondsKey>> = rules.seconds.map((s) => ({
    value: s,
    label: secondsLabel(s),
  }))
  const optionsOptions: ReadonlyArray<SegmentedOption<OptionsKey>> = rules.options.map((o) => ({
    value: o,
    label: o,
  }))
  const languageOptions = rules.allowBothLanguages
    ? LANGUAGES
    : LANGUAGES.filter((l) => l.value !== 'both')

  /** Trocar de formato leva os controles para os defaults dele e troca o tema
   * para um do namespace certo — um tema quiz-* não tem sementes de dilema, e
   * um choice-* não tem ângulo de pergunta com resposta. */
  function switchFormat(next: QuizVideoType) {
    if (next === videoType) return
    const nextRules = FORMAT_RULES[next]
    const defaults = formatDefaults(next)
    form.setValue('video_type', next)
    form.setValue('num_questions', defaults.num_questions)
    form.setValue('options_per_question', defaults.options_per_question)
    form.setValue('seconds_per_question', defaults.seconds_per_question)
    // ``random`` e ``custom`` valem nos dois formatos; o backend sorteia no
    // namespace do formato escolhido.
    if (theme !== 'custom' && theme !== 'random' && !theme.startsWith(nextRules.themePrefix)) {
      const first = allThemes.find((t: Theme) => t.slug.startsWith(nextRules.themePrefix))
      form.setValue('theme', first?.slug ?? '', { shouldValidate: true })
    }
    if (language === 'both' && !nextRules.allowBothLanguages) {
      form.setValue('language', 'pt-br')
    }
  }

  const onSubmit = form.handleSubmit((values) => {
    const payload: QuizInput = {
      theme: values.theme,
      channel: channel || null,
      count: values.count,
      language: values.language,
      video_type: values.video_type,
      num_questions: values.num_questions,
      seconds_per_question: values.seconds_per_question,
      difficulty: values.difficulty,
      options_per_question: values.options_per_question,
      force: false,
      // Só com o tema custom, como o preset (``formToPayload``): um prompt
      // que ficou no campo escondido não pode ir junto com um tema sorteado.
      custom_prompt: values.theme === 'custom' ? (values.custom_prompt ?? '') : '',
      tone: values.tone ?? null,
      tts_voice_pt: values.tts_voice_pt || null,
      tts_voice_en: values.tts_voice_en || null,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Brain aria-hidden /> {rules.kicker}
          </div>
          <h1 className={styles.title}>{rules.title}</h1>
          <p className={styles.subtitle}>{rules.subtitle}</p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="quiz"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Shapes />} label="Formato">
              <Segmented<QuizVideoType>
                options={VIDEO_TYPES}
                value={videoType}
                onValueChange={switchFormat}
                aria-label="Formato do quiz"
              />
            </Row>

            <Row icon={<Hash />} label="Tema" htmlFor="quiz-theme">
              <SelectRoot
                value={theme}
                onValueChange={(v) => {
                  form.setValue('theme', v, { shouldValidate: true })
                }}
              >
                <SelectTrigger id="quiz-theme" aria-label="Tema">
                  <SelectValue placeholder={themesPending ? 'Carregando...' : 'Escolha um tema'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">{rules.randomLabel}</SelectItem>
                  <SelectItem value={CUSTOM_THEME_OPTION.value}>{CUSTOM_THEME_OPTION.label}</SelectItem>
                  {themes.map((t: Theme) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {form.formState.errors.theme ? (
                <span className={styles.errorText}>{form.formState.errors.theme.message}</span>
              ) : null}
            </Row>

            {theme === 'custom' ? (
              <Row icon={<Wand2 />} label="Prompt" htmlFor="quiz-prompt">
                <textarea
                  id="quiz-prompt"
                  className={cn(
                    styles.textarea,
                    form.formState.errors.custom_prompt && styles.textareaError,
                  )}
                  placeholder={rules.promptPlaceholder}
                  rows={3}
                  {...form.register('custom_prompt')}
                />
                <SuggestPromptButton
                  factory="quiz"
                  language={language}
                  channel={channel}
                  pending={suggestPrompt.isPending}
                  error={suggestPrompt.error}
                  onClick={() => {
                    suggestPrompt.mutate(
                      // Só a Escolha tem variante no sugeridor (temas de
                      // dilemas). A trivia não manda o campo: o backend antes
                      // do pull recusa chave desconhecida (extra="forbid").
                      {
                        factory: 'quiz',
                        language,
                        channel: channel || null,
                        video_type: videoType === 'choice' ? 'choice' : null,
                      },
                      {
                        onSuccess: (prompt) => {
                          form.setValue('custom_prompt', prompt, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        },
                      },
                    )
                  }}
                />
                {form.formState.errors.custom_prompt ? (
                  <span className={styles.errorText}>
                    {form.formState.errors.custom_prompt.message}
                  </span>
                ) : null}
              </Row>
            ) : null}

            <Row icon={<HelpCircle />} label={rules.roundsLabel}>
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={numQuestions <= minRounds}
                  onClick={() => {
                    form.setValue('num_questions', Math.max(minRounds, numQuestions - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{numQuestions}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={numQuestions >= maxRounds}
                  onClick={() => {
                    form.setValue('num_questions', Math.min(maxRounds, numQuestions + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            <Row icon={<Clock />} label={rules.secondsLabel}>
              <Segmented<SecondsKey>
                options={secondsOptions}
                value={String(secondsPerQuestion) as SecondsKey}
                onValueChange={(v) => {
                  form.setValue('seconds_per_question', Number(v))
                }}
                aria-label={rules.secondsLabel}
              />
            </Row>

            {rules.hasDifficulty ? (
              <Row icon={<Gauge />} label="Dificuldade">
                <Segmented<DifficultyKey>
                  options={DIFFICULTIES}
                  value={difficulty}
                  onValueChange={(v) => {
                    form.setValue('difficulty', v)
                  }}
                  aria-label="Dificuldade"
                />
              </Row>
            ) : null}

            <Row icon={<ListChecks />} label="Alternativas">
              <Segmented<OptionsKey>
                options={optionsOptions}
                value={String(optionsPerQuestion) as OptionsKey}
                onValueChange={(v) => {
                  form.setValue('options_per_question', Number(v))
                }}
                aria-label="Alternativas por pergunta"
              />
            </Row>

            <Row icon={<Layers />} label="Quantidade">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={count <= 1}
                  onClick={() => {
                    form.setValue('count', Math.max(1, count - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{count}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={count >= 10}
                  onClick={() => {
                    form.setValue('count', Math.min(10, count + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Globe />} label="Idioma">
                  <Segmented<LanguageKey>
                    options={languageOptions}
                    value={language}
                    onValueChange={(v) => {
                      form.setValue('language', v)
                    }}
                    aria-label="Idioma"
                  />
                </AdvSection>

                <AdvSection icon={<MessageSquare />} label="Tom">
                  <Segmented<ToneKey>
                    options={TONES}
                    value={tone ?? ('' as ToneKey)}
                    onValueChange={(v) => {
                      form.setValue('tone', v)
                    }}
                    aria-label="Tom"
                  />
                </AdvSection>

                <NarratorPicker
                  language={language}
                  voicePt={voicePt ?? ''}
                  voiceEn={voiceEn ?? ''}
                  onVoicePtChange={(v) => {
                    form.setValue('tts_voice_pt', v)
                  }}
                  onVoiceEnChange={(v) => {
                    form.setValue('tts_voice_en', v)
                  }}
                />

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          {knobErrors.length > 0 ? (
            <div role="alert" className={styles.errorText}>
              {knobErrors.join(' · ')}
            </div>
          ) : null}

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="quiz" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}
